import { mapGetters } from 'vuex';
import dashboardPartnerData from '@/mixins/dashboardPartnerData.js';

export default {
  mixins: [dashboardPartnerData],
  data() {
    return {
      suggestedPartners: []
    };
  },
  computed: {
    ...mapGetters({
      reauthorizationClosed: 'profile/reauthorization/reauthorizationClosed'
    }),
    hasSuggestedPartners() {
      return this.suggestedPartners.length > 0;
    },
    daysUntilBenefitEnds() {
      const benefitEndDate = new Date(this.$auth.user?.benefit?.validTo);
      const currentDate = new Date();
      const daysDifference = Math.ceil((benefitEndDate - currentDate) / (1000 * 60 * 60 * 24));
      return daysDifference.toString();
    },
    isForceViewCases() {
      const daysUntilBenefitEnds = parseInt(this.daysUntilBenefitEnds);
      return daysUntilBenefitEnds === 30 || daysUntilBenefitEnds === 15 || this.hadBenefitBefore;
    },
    viewReauthorization() {
      const daysUntilBenefitEnds = parseInt(this.daysUntilBenefitEnds);
      if (daysUntilBenefitEnds > 30) {
        return false;
      }
      if (!this.reauthorizationClosed && daysUntilBenefitEnds < 30) {
        return true;
      }
    },
    benefitValidTo() {
      if (this.$auth.user?.benefit?.validTo) {
        const date = Date.parse(this.$auth.user?.benefit?.validTo);
        const dateFormat = new Intl.DateTimeFormat(this.$i18n.locale);
        return dateFormat.format(date);
      } else {
        return;
      }
    },
    isBenefitEndingInMonth() {
      const days = Number(this.daysUntilBenefitEnds);
      if (!Number.isFinite(days)) {
        return false;
      }
      return days <= 30;
    },
    isB2C() {
      return !this.$auth.user?.benefit?.active;
    },
    isPending() {
      return this.$auth.user?.benefit?.status === 'pending';
    },
    reauthorizationCase() {
      if (this.isBenefitEndingInMonth) {
        return 'prolong';
      }
      if (this.isPending) {
        return 'pending';
      }
      if (this.hasBenefitByFamily) {
        return 'family';
      }
      if (this.hasBenefitByFamily && this.isB2C) {
        return 'familyExpired';
      }
      if (this.hadBenefitBefore) {
        return 'ended';
      }
      return 'authorize';
    },
    benefitActivationCase() {
      if (this.isBenefitEndingInMonth) {
        return 'reauthorization_while_benefit_active';
      }
      if (this.hadBenefitBefore) {
        return 'reauthorization_while_benefit_ended';
      }
      if (this.$auth.user?.benefit?.active && !this.isBenefitEndingInMonth) {
        return 'changing_partner';
      }
      return 'authorization';
    },
    profileButtonCase() {
      if (this.isBenefitEndingInMonth) {
        return 'prolong';
      }
      if (this.hadBenefitBefore) {
        return 'ended';
      }
      if (this.isB2C && this.hadBenefitBefore) {
        return 'reauthorize';
      }
      if (this.$auth.user?.benefit?.active) {
        return 'manage';
      }
      return 'authorize';
    },
    hadBenefitBefore() {
      return (
        this.$auth.user?.benefit?.partnerUrl !== undefined &&
        !this.$auth.user?.benefit?.active &&
        !this.isPending
      );
    },
    hasBenefitByFamily() {
      return this.$auth.user?.invitation?.host !== null;
    }
  },
  methods: {
    async findSuggestedPartners({ showErrorModal = false } = {}) {
      const url = `/v3/fe/partners?region=${this.$domainRegion}&email=${this.$auth.user?.email}`;
      try {
        const data = await this.$http.get(url);
        this.suggestedPartners = Object.freeze(data);
      } catch (err) {
        this.suggestedPartners = [];
        this.$store.dispatch(
          'errorModal/handleError',
          { error: err, showModal: showErrorModal, code: '0x00000084' },
          { root: true }
        );
      }
    },
    async handleEmailDomainAuth() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      this.setDialogLoading(true);

      if (!this.partnerSelected?.identifier) {
        this.handleError({ error: new Error('Missing partnerIdentifier'), code: '0x00000064' });
        this.loading = false;
        this.setDialogLoading(false);
        return;
      }

      try {
        await this.$http.post('/v3/fe/web-user/benefit/authorization', {
          partnerIdentifier: this.partnerSelected.identifier
        });

        await this.$auth.fetchUser();

        await this.$router.push(
          this.$localePath({
            name: this.currentPath,
            query: {
              ...this.$route.query,
              authStep: 'final'
            }
          })
        );
      } catch (err) {
        // Known error message → expected business error → no Sentry
        // No error message (5xx, network, unknown) → Sentry
        if (!err.data?.error) {
          this.handleError({ error: err, code: '0x00000088' });
        }
      } finally {
        this.loading = false;
        this.setDialogLoading(false);
      }
    }
  }
};
